/**
 * Booking insights — hospitality KPIs (occupancy, ADR, RevPAR) and channel mix.
 * Uses stay dates overlapping the selected period; no schema changes.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BOOKING_SOURCES,
  BOOKING_PAYMENT_OPTIONS,
  parseBookingFiscal,
  type BookingSource,
  type BookingPaymentOption,
} from '@cafe-pos/shared';
import type { Database } from '@cafe-pos/supabase';
import { BedDouble } from 'lucide-react';
import { supabase } from '../supabase.js';
import {
  Callout,
  Card,
  DateRange,
  EmptyState,
  PageHeader,
  resolveDateRangeDates,
  Stat,
  Stats,
  type DateRangeValue,
} from '../ui/index.js';
import { SOURCE_COLORS } from './BookingsView.js';

const AMD = (n: number) => `${Math.round(n).toLocaleString('en-US')} ֏`;
const PCT = (n: number) => `${n.toFixed(1)}%`;
type Period = DateRangeValue;

type BookingRow = Pick<
  Database['public']['Tables']['bookings']['Row'],
  | 'id'
  | 'room_id'
  | 'guest_name'
  | 'check_in'
  | 'check_out'
  | 'source'
  | 'amount_amd'
  | 'payment_option'
  | 'payment_status'
  | 'payment_method'
  | 'fiscal_receipt'
  | 'paid_at'
  | 'created_at'
  | 'status'
>;

type RoomRow = Pick<Database['public']['Tables']['rooms']['Row'], 'id' | 'name' | 'active'>;

const TS: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--text)',
  boxShadow: 'var(--shadow-sm)',
};

const PAYMENT_COLORS: Record<string, string> = {
  paid: 'var(--chart-6)',
  unpaid: 'var(--chart-3)',
  pending: 'var(--chart-1)',
  failed: '#ff3b30',
};

const OPTION_COLORS: Record<string, string> = {
  on_site: 'var(--chart-1)',
  online: 'var(--chart-2)',
  free: 'var(--chart-3)',
};

const FETCH_PAGE = 1000;

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function periodBounds(p: Period): {
  start: Date;
  end: Date;
  days: number;
  startYmd: string;
  endExclusiveYmd: string;
} {
  const [start, end] = resolveDateRangeDates(p);
  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000));
  const endExclusive = new Date(end);
  endExclusive.setDate(endExclusive.getDate() + 1);
  endExclusive.setHours(0, 0, 0, 0);
  return {
    start,
    end,
    days,
    startYmd: isoDate(start),
    endExclusiveYmd: isoDate(endExclusive),
  };
}

function nightsBetween(checkIn: string, checkOut: string): number {
  return Math.max(
    1,
    Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86_400_000),
  );
}

/** Room-nights of a stay that fall inside [periodStart, periodEnd]. */
function nightsInPeriod(
  checkIn: string,
  checkOut: string,
  periodStart: Date,
  periodEnd: Date,
): number {
  let count = 0;
  const ci = new Date(checkIn + 'T00:00:00');
  const co = new Date(checkOut + 'T00:00:00');
  for (let d = new Date(ci); d < co; d.setDate(d.getDate() + 1)) {
    if (d >= periodStart && d <= periodEnd) count += 1;
  }
  return count;
}

function enumerateDays(start: Date, end: Date): string[] {
  const out: string[] = [];
  const d = new Date(start);
  d.setHours(0, 0, 0, 0);
  const last = new Date(end);
  last.setHours(0, 0, 0, 0);
  while (d <= last) {
    out.push(isoDate(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

async function fetchPaged<T>(
  query: (
    from: number,
    to: number,
  ) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let offset = 0; ; offset += FETCH_PAGE) {
    const { data, error } = await query(offset, offset + FETCH_PAGE - 1);
    if (error) throw new Error(error.message);
    const batch = data ?? [];
    rows.push(...batch);
    if (batch.length < FETCH_PAGE) break;
  }
  return rows;
}

export function BookingsInsightsView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const [period, setPeriod] = useState<Period>({ preset: '30d' });
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [rooms, setRooms] = useState<RoomRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const bounds = useMemo(() => periodBounds(period), [period]);

  useEffect(() => {
    if (!supabase || !clientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const db = supabase;
        const [bookingRows, roomRows] = await Promise.all([
          fetchPaged((offset, limit) =>
            db
              .from('bookings')
              .select(
                'id, room_id, guest_name, check_in, check_out, source, amount_amd, payment_option, payment_status, payment_method, fiscal_receipt, paid_at, created_at, status',
              )
              .eq('client_id', clientId)
              .lt('check_in', bounds.endExclusiveYmd)
              .gt('check_out', bounds.startYmd)
              .order('check_in', { ascending: false })
              .range(offset, limit),
          ),
          db.from('rooms').select('id, name, active').eq('client_id', clientId),
        ]);
        if (cancelled) return;
        setBookings(bookingRows as BookingRow[]);
        if (roomRows.error) throw new Error(roomRows.error.message);
        setRooms((roomRows.data ?? []) as RoomRow[]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientId, bounds.startYmd, bounds.endExclusiveYmd]);

  const roomName = useMemo(() => {
    const map = new Map(rooms.map((r) => [r.id, r.name]));
    return (id: string) => map.get(id) ?? '—';
  }, [rooms]);

  const activeRoomCount = useMemo(
    () => rooms.filter((r) => r.active).length || rooms.length,
    [rooms],
  );

  const stats = useMemo(() => {
    const availableRoomNights = bounds.days * activeRoomCount;
    let soldRoomNights = 0;
    let paidRoomNights = 0;
    let paidRevenue = 0;
    let bookedRevenue = 0;
    let outstanding = 0;
    let confirmedStays = 0;
    let cancelledStays = 0;
    let compStays = 0;
    let compNights = 0;
    let totalLos = 0;
    let losCount = 0;

    const bySource = new Map<string, { bookings: number; nights: number; revenue: number }>();
    const byRoom = new Map<
      string,
      { name: string; bookings: number; nights: number; revenue: number }
    >();
    const byPaymentStatus = new Map<string, { count: number; amount: number }>();
    const byOption = new Map<string, { bookings: number; nights: number; revenue: number }>();
    const dailyRevenue = new Map<string, number>();

    for (const s of BOOKING_SOURCES) {
      bySource.set(s, { bookings: 0, nights: 0, revenue: 0 });
    }
    for (const o of BOOKING_PAYMENT_OPTIONS) {
      byOption.set(o, { bookings: 0, nights: 0, revenue: 0 });
    }
    for (const ps of ['paid', 'unpaid', 'pending', 'failed'] as const) {
      byPaymentStatus.set(ps, { count: 0, amount: 0 });
    }

    for (const day of enumerateDays(bounds.start, bounds.end)) {
      dailyRevenue.set(day, 0);
    }

    for (const b of bookings) {
      const isFree = b.payment_option === 'free';
      const amd = isFree ? 0 : Number(b.amount_amd) || 0;
      const isCancelled = b.status === 'cancelled';
      const isPaid = !isFree && b.payment_status === 'paid';
      const nights = nightsInPeriod(b.check_in, b.check_out, bounds.start, bounds.end);
      const stayNights = nightsBetween(b.check_in, b.check_out);

      if (isCancelled) {
        cancelledStays += 1;
        continue;
      }

      confirmedStays += 1;
      totalLos += stayNights;
      losCount += 1;
      soldRoomNights += nights;
      if (isFree) {
        compStays += 1;
        compNights += nights;
      } else {
        bookedRevenue += amd;
      }

      if (!isFree) {
        const psStat = byPaymentStatus.get(b.payment_status) ?? { count: 0, amount: 0 };
        psStat.count += 1;
        psStat.amount += amd;
        byPaymentStatus.set(b.payment_status, psStat);
      }

      if (isPaid) {
        paidRevenue += amd;
        paidRoomNights += nights;
        const paidDay = b.paid_at
          ? isoDate(new Date(b.paid_at))
          : b.check_in;
        if (dailyRevenue.has(paidDay)) {
          dailyRevenue.set(paidDay, (dailyRevenue.get(paidDay) ?? 0) + amd);
        }
      } else {
        outstanding += amd;
      }

      const src = (BOOKING_SOURCES as readonly string[]).includes(b.source) ? b.source : 'other';
      const srcStat = bySource.get(src)!;
      srcStat.bookings += 1;
      srcStat.nights += nights;
      if (isPaid) srcStat.revenue += amd;
      bySource.set(src, srcStat);

      const opt = (BOOKING_PAYMENT_OPTIONS as readonly string[]).includes(b.payment_option)
        ? b.payment_option
        : 'on_site';
      const optStat = byOption.get(opt)!;
      optStat.bookings += 1;
      optStat.nights += nights;
      if (isPaid) optStat.revenue += amd;
      byOption.set(opt, optStat);

      const roomStat = byRoom.get(b.room_id) ?? {
        name: roomName(b.room_id),
        bookings: 0,
        nights: 0,
        revenue: 0,
      };
      roomStat.bookings += 1;
      roomStat.nights += nights;
      if (isPaid) roomStat.revenue += amd;
      byRoom.set(b.room_id, roomStat);
    }

    const occupancyPct =
      availableRoomNights > 0 ? (soldRoomNights / availableRoomNights) * 100 : 0;
    const adr = paidRoomNights > 0 ? paidRevenue / paidRoomNights : 0;
    const revpar = availableRoomNights > 0 ? paidRevenue / availableRoomNights : 0;
    const avgLos = losCount > 0 ? totalLos / losCount : 0;
    const cancelRate =
      confirmedStays + cancelledStays > 0
        ? (cancelledStays / (confirmedStays + cancelledStays)) * 100
        : 0;
    const collectionRate = bookedRevenue > 0 ? (paidRevenue / bookedRevenue) * 100 : 0;

    const sourceRows = BOOKING_SOURCES.map((s) => ({
      source: s as BookingSource,
      ...bySource.get(s)!,
    }))
      .filter((r) => r.bookings > 0)
      .sort((a, b) => b.revenue - a.revenue);

    const roomRows = [...byRoom.values()].sort((a, b) => b.revenue - a.revenue);
    const maxRoomRev = Math.max(...roomRows.map((r) => r.revenue), 1);

    const paymentRows = [...byPaymentStatus.entries()]
      .map(([status, v]) => ({ status, ...v }))
      .filter((r) => r.count > 0);

    const optionRows = BOOKING_PAYMENT_OPTIONS.map((o) => ({
      option: o as BookingPaymentOption,
      ...byOption.get(o)!,
    })).filter((r) => r.bookings > 0);

    const trendData = enumerateDays(bounds.start, bounds.end).map((day) => ({
      day,
      label: new Date(day + 'T00:00:00').toLocaleDateString(
        i18n.language === 'hy' ? 'hy-AM' : 'en-US',
        { month: 'short', day: 'numeric' },
      ),
      revenue: dailyRevenue.get(day) ?? 0,
    }));

    const channelPie = sourceRows
      .filter((r) => r.revenue > 0)
      .map((r) => ({
        name: t(`bookings.source.${r.source}`),
        value: r.revenue,
        source: r.source,
      }));

    const recentPaid = bookings
      .filter(
        (b) => b.status === 'confirmed' && b.payment_status === 'paid' && b.payment_option !== 'free',
      )
      .sort((a, b) => (b.paid_at ?? b.check_in).localeCompare(a.paid_at ?? a.check_in))
      .slice(0, 20);

    return {
      occupancyPct,
      adr,
      revpar,
      paidRevenue,
      confirmedStays,
      cancelledStays,
      cancelRate,
      avgLos,
      collectionRate,
      outstanding,
      soldRoomNights,
      availableRoomNights,
      bookedRevenue,
      compStays,
      compNights,
      sourceRows,
      roomRows,
      maxRoomRev,
      paymentRows,
      optionRows,
      trendData,
      channelPie,
      recentPaid,
      hasData: confirmedStays + cancelledStays > 0,
    };
  }, [bookings, bounds, activeRoomCount, roomName, t, i18n.language]);

  const fmtDate = (iso: string) =>
    new Date(iso + (iso.includes('T') ? '' : 'T00:00:00')).toLocaleDateString(
      i18n.language === 'hy' ? 'hy-AM' : 'en-US',
      { day: 'numeric', month: 'short' },
    );

  return (
    <section className="booking-insights-page">
      <PageHeader
        title={t('bookingInsights.title')}
        subtitle={t('bookingInsights.subtitle')}
        actions={
          <DateRange value={period} onChange={setPeriod} />
        }
      />

      {error && (
        <Callout tone="danger" title={t('bookings.couldntLoad')}>
          {error}
        </Callout>
      )}

      {loading ? (
        <div className="dash-loading">
          <div className="spinner" /> {t('common.loading')}
        </div>
      ) : !stats.hasData ? (
        <Card>
          <EmptyState icon={<BedDouble />} title={t('bookingInsights.noData')} />
        </Card>
      ) : (
        <div className="insights-grid">
          <Stats>
            <Stat
              label={t('bookingInsights.kpiOccupancy')}
              value={PCT(stats.occupancyPct)}
              sub={`${stats.soldRoomNights} / ${stats.availableRoomNights} ${t('bookingInsights.roomNights')}`}
            />
            <Stat
              label={t('bookingInsights.kpiAdr')}
              value={stats.adr > 0 ? AMD(stats.adr) : '—'}
              sub={t('bookingInsights.adrHint')}
            />
            <Stat
              label={t('bookingInsights.kpiRevpar')}
              value={stats.revpar > 0 ? AMD(stats.revpar) : '—'}
              sub={t('bookingInsights.revparHint')}
            />
            <Stat
              label={t('bookingInsights.kpiRevenue')}
              value={AMD(stats.paidRevenue)}
              sub={t('bookingInsights.revenueHint')}
            />
            <Stat
              label={t('bookingInsights.kpiStays')}
              value={stats.confirmedStays}
              sub={
                stats.cancelledStays > 0
                  ? t('bookingInsights.cancelledCount', { count: stats.cancelledStays })
                  : t('bookingInsights.staysHint')
              }
            />
          </Stats>

          <Stats>
            <Stat
              label={t('bookingInsights.kpiAvgLos')}
              value={
                stats.avgLos > 0
                  ? t('bookingInsights.nightsCount', { count: stats.avgLos.toFixed(1) })
                  : '—'
              }
            />
            <Stat
              label={t('bookingInsights.kpiCollection')}
              value={PCT(stats.collectionRate)}
              sub={`${AMD(stats.paidRevenue)} / ${AMD(stats.bookedRevenue)}`}
            />
            <Stat
              label={t('bookingInsights.kpiOutstanding')}
              value={AMD(stats.outstanding)}
              sub={
                <span className={stats.cancelRate > 15 ? 'text-danger' : undefined}>
                  {t('bookingInsights.kpiCancelRate')}: {PCT(stats.cancelRate)}
                </span>
              }
            />
            <Stat
              label={t('bookingInsights.kpiComplimentary')}
              value={t('bookingInsights.nightsCount', { count: stats.compNights })}
              sub={
                stats.compStays > 0
                  ? `${stats.compStays} ${t('bookingInsights.staysShort')} · ${t('bookingInsights.compHint')}`
                  : t('bookingInsights.compHint')
              }
            />
          </Stats>

          <div className="dash-grid">
            <div className="chart-card">
              <div className="chart-title">{t('bookingInsights.trendTitle')}</div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats.trendData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: 'var(--text-subtle)' }}
                    interval={bounds.days > 60 ? 6 : bounds.days > 14 ? 2 : 0}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'var(--text-subtle)' }}
                    tickFormatter={(v) =>
                      Number(v) >= 1000 ? `${Math.round(Number(v) / 1000)}k` : String(v)
                    }
                    width={42}
                  />
                  <Tooltip
                    contentStyle={TS}
                    formatter={(v) => [AMD(Number(v) || 0), t('bookingInsights.colRevenue')]}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.day
                        ? fmtDate(String(payload[0].payload.day))
                        : ''
                    }
                  />
                  <Bar dataKey="revenue" fill="var(--info)" radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <div className="chart-title">{t('bookingInsights.channelMixTitle')}</div>
              {stats.channelPie.length === 0 ? (
                <div className="chart-empty">{t('bookingInsights.noPaidBySource')}</div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={stats.channelPie}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={72}
                        paddingAngle={2}
                      >
                        {stats.channelPie.map((entry) => (
                          <Cell key={entry.source} fill={SOURCE_COLORS[entry.source]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={TS} formatter={(v) => AMD(Number(v) || 0)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pay-legend">
                    {stats.sourceRows.map((r) => (
                      <div key={r.source} className="pay-legend-row">
                        <span
                          className="pay-legend-dot"
                          style={{ background: SOURCE_COLORS[r.source] }}
                        />
                        <span className="pay-legend-name">{t(`bookings.source.${r.source}`)}</span>
                        <span className="pay-legend-pct">
                          {stats.paidRevenue > 0
                            ? `${Math.round((r.revenue / stats.paidRevenue) * 100)}%`
                            : '—'}
                        </span>
                        <span className="pay-legend-val">{AMD(r.revenue)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="booking-insights-split">
            <div className="chart-card">
              <div className="chart-title">{t('bookingInsights.roomPerformanceTitle')}</div>
              {stats.roomRows.length === 0 ? (
                <div className="chart-empty">{t('bookingInsights.noData')}</div>
              ) : (
                <div className="top-list">
                  {stats.roomRows.map((r, i) => (
                    <div key={r.name} className="top-row">
                      <span className="top-rank">{i + 1}</span>
                      <div className="top-info">
                        <div className="top-name">{r.name}</div>
                        <div className="top-bar-track">
                          <div
                            className="top-bar-fill"
                            style={{ width: `${(r.revenue / stats.maxRoomRev) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className="top-meta">
                        <span className="top-revenue">{AMD(r.revenue)}</span>
                        <span className="muted" style={{ fontSize: 11 }}>
                          {r.bookings} {t('bookingInsights.staysShort')} · {r.nights}{' '}
                          {t('bookingInsights.nightsShort')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="chart-card">
              <div className="chart-title">{t('bookingInsights.paymentMixTitle')}</div>
              <div className="pay-legend" style={{ marginTop: 0 }}>
                {stats.paymentRows.map((r) => (
                  <div key={r.status} className="pay-legend-row">
                    <span
                      className="pay-legend-dot"
                      style={{
                        background: PAYMENT_COLORS[r.status] ?? 'var(--chart-4)',
                      }}
                    />
                    <span className="pay-legend-name">
                      {t(`bookings.paymentStatus.${r.status}`)}
                    </span>
                    <span className="pay-legend-pct">{r.count}</span>
                    <span className="pay-legend-val">{AMD(r.amount)}</span>
                  </div>
                ))}
              </div>
              {stats.optionRows.length > 0 && (
                <>
                  <div className="chart-title" style={{ marginTop: 20, marginBottom: 10 }}>
                    {t('bookingInsights.byPaymentOption')}
                  </div>
                  <div className="pay-legend">
                    {stats.optionRows.map((r) => (
                      <div key={r.option} className="pay-legend-row">
                        <span
                          className="pay-legend-dot"
                          style={{ background: OPTION_COLORS[r.option] ?? 'var(--info)' }}
                        />
                        <span className="pay-legend-name">
                          {t(`bookings.paymentOptionValue.${r.option}`)}
                        </span>
                        <span className="pay-legend-pct">
                          {r.bookings} · {r.nights} {t('bookingInsights.nightsShort')}
                        </span>
                        <span className="pay-legend-val">{AMD(r.revenue)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="chart-card insights-wide">
            <div className="chart-title">{t('bookingInsights.recentPaid')}</div>
            {stats.recentPaid.length === 0 ? (
              <div className="chart-empty">{t('bookingInsights.noPaidYet')}</div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('bookingInsights.colGuest')}</th>
                      <th>{t('bookingInsights.colRoom')}</th>
                      <th>{t('bookingInsights.colDates')}</th>
                      <th>{t('bookingInsights.colSource')}</th>
                      <th>{t('bookingInsights.colAmount')}</th>
                      <th>{t('bookingInsights.colTender')}</th>
                      <th>{t('bookingInsights.colFiscal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentPaid.map((b) => {
                      const fiscal = parseBookingFiscal(b.fiscal_receipt);
                      return (
                        <tr key={b.id}>
                          <td>{b.guest_name}</td>
                          <td>{roomName(b.room_id)}</td>
                          <td>
                            {fmtDate(b.check_in)} → {fmtDate(b.check_out)}
                          </td>
                          <td>{t(`bookings.source.${b.source as BookingSource}`)}</td>
                          <td>{AMD(Number(b.amount_amd) || 0)}</td>
                          <td>
                            {b.payment_method
                              ? t(`bookings.tender.${b.payment_method}`)
                              : '—'}
                          </td>
                          <td>{fiscal?.fiscal ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
